import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Sparkles,
  ClipboardList,
  PenLine,
  ClipboardCheck,
  Zap,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { useAuth } from "@/features/authentication";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { reportError } from "@/utils/observability";
import { visionService } from "@/services/vision-service";
import { createToast } from "vercel-toast";
import { useAIMode } from "./hooks/useAIMode";
import { ImportDealContext, useImportDealState } from "./hooks/useImportDeal";
import { reconcileDiagnosisWithBrief } from "./utils/extractionReconciliation";
import DiagnosisStep from "./steps/DiagnosisStep";
import LocationStep from "./steps/LocationStep";
import AdjustmentsStep from "./steps/AdjustmentsStep";
import ImagesStep from "./steps/ImagesStep";
import ProposalStep from "./steps/ProposalStep";
import ReviewStep from "./steps/ReviewStep";
import AIModeStepperBar from "./preview/AIModeStepperBar";
import DiagnosisPreview from "./preview/DiagnosisPreview";
import LocationPreview from "./preview/LocationPreview";
import AdjustmentsPreview from "./preview/AdjustmentsPreview";
import ImagesPreview from "./preview/ImagesPreview";
import ProposalPreview from "./preview/ProposalPreview";
import { STEPS } from "./constants";
import "./ai-mode-wizard.css";
import { TicketSheet } from "@/features/vision-360/ticket";
import type { TicketRoom } from "@/features/vision-360/ticket";
import { formatCityUf } from "../utils/format-city-uf";
import { getDefaultSearchSource } from "@/services/vision-service";
import type {
  DiagnosisData,
  LocationData,
  AdjustmentsData,
  AIModeState,
  LivePreview,
  SearchSource,
  ProposalPayload,
} from "./types";

function PreviewPanel({
  step,
  preview,
  state,
  liveAdjustments,
  onSpaceMarkerClick,
  selectedSpaceId,
}: {
  step: number;
  preview: LivePreview;
  state: AIModeState;
  liveAdjustments: AdjustmentsData | null;
  onSpaceMarkerClick?: (id: string) => void;
  selectedSpaceId?: string | null;
}) {
  if (step === 0) return <DiagnosisPreview preview={preview} />;
  if (step === 1)
    return (
      <LocationPreview
        preview={preview}
        onSpaceMarkerClick={onSpaceMarkerClick}
      />
    );
  if (step === 2)
    return (
      <AdjustmentsPreview state={state} liveAdjustments={liveAdjustments} />
    );
  if (step === 3)
    return (
      <ImagesPreview
        state={state}
        liveAdjustments={liveAdjustments}
        selectedSpaceId={selectedSpaceId}
      />
    );
  if (step === 4) return <ProposalPreview state={state} />;
  return null;
}

export default function AIModeWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userInfo } = useAuth();
  const {
    state,
    nextStep,
    prevStep,
    reset,
    updateDiagnosis,
    updateDiagnosisPartial,
    updateLocation,
    updateLocationPartial,
    updateAdjustments,
    enterQuickSearch,
    exitQuickSearch,
    initFromProposal,
  } = useAIMode();
  const { currentStep, completedSteps } = state;
  const isQuickMode = state.mode === "quick";

  const editingId = searchParams.get("edit");
  const [editingProposal, setEditingProposal] =
    useState<ProposalPayload | null>(null);
  const [isLoadingEdit, setIsLoadingEdit] = useState(!!editingId);

  // Memoizado para evitar nova referência de array a cada render (causaria re-disparo
  // de effects no LocationStep que têm originalSpaces como dependência)
  const editOriginalSpaces = useMemo(() => {
    const loc = editingProposal?.state.location;
    if (!loc?.rankedSpaces?.length || !loc.selectedSpaceIds?.length)
      return undefined;
    const selectedIds = new Set(loc.selectedSpaceIds);
    return loc.rankedSpaces.filter((s) => selectedIds.has(s.id));
  }, [editingProposal?.state.location]);

  useEffect(() => {
    if (!editingId) return;
    visionService
      .getProposal(editingId)
      .then((proposal) => {
        // Proposals saved before the type toggle existed (or reclassified by
        // the backend) carry proposal_type only at the top level — seed it into
        // adjustments so the Ajustes toggle prefills and re-saving keeps it.
        initFromProposal(
          proposal.state.adjustments
            ? {
                ...proposal.state,
                adjustments: {
                  ...proposal.state.adjustments,
                  proposalType:
                    proposal.state.adjustments.proposalType ??
                    proposal.proposalType,
                },
              }
            : proposal.state,
        );
        setEditingProposal(proposal);
        sendTrackDataServer({
          eventName: "VISION_AI_MODE_PROPOSAL_EDIT_STARTED",
          productType: "PO",
          url: window.location.pathname,
          data: {
            proposal_uuid: editingId,
            user_id: userInfo?.user?.id ?? null,
            email: userInfo?.profile?.email ?? null,
            version_number: proposal.versionNumber ?? 1,
          },
        });
      })
      .catch((error) => {
        reportError(error, { operation: "getProposal", proposalId: editingId });
        createToast("Não foi possível carregar a proposta para edição.", {
          type: "error",
          timeout: 4000,
        });
        navigate(DEFAULT_ROUTES.VISION_360_AI_MODE);
      })
      .finally(() => setIsLoadingEdit(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initialLivePreview: LivePreview = {
    companyName: "",
    targetCity: "",
    positionsMin: "",
    positionsMax: "",
    dealImported: false,
    location: null,
    radiusKm: null,
    activeSpaceId: null,
  };

  const [showNewSearchConfirmOpen, setShowNewSearchConfirmOpen] =
    useState(false);
  const [isTicketSheetOpen, setIsTicketSheetOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const getLocationFormStateRef = useRef<(() => Partial<LocationData>) | null>(
    null,
  );
  const onMarkerClickRef = useRef<((id: string) => void) | null>(null);

  const [livePreview, setLivePreview] = useState<LivePreview>({
    companyName: state.diagnosis?.companyName ?? "",
    targetCity: state.diagnosis?.targetCity ?? "",
    positionsMin: state.diagnosis?.positionsMin ?? "",
    positionsMax: state.diagnosis?.positionsMax ?? "",
    dealImported: !!state.diagnosis?.companyName,
    location: state.location?.location ?? null,
    radiusKm: state.location?.radiusKm ?? null,
    activeSpaceId: null,
  });

  const handleDiagnosisSyncFromLocation = useCallback(
    (partial: {
      positionsMin: number | "";
      positionsMax: number | "";
      targetCity: string;
      selectedLocation: { label: string; lat: number; lng: number } | null;
      includeUnavailableWithinPeriod?: number;
    }) => {
      updateDiagnosisPartial(partial);
    },
    [updateDiagnosisPartial],
  );

  const handlePreviewUpdate = useCallback((data: Partial<LivePreview>) => {
    setLivePreview((prev) => ({ ...prev, ...data }));
  }, []);

  const [liveAdjustments, setLiveAdjustments] =
    useState<AdjustmentsData | null>(state.adjustments);

  const handleAdjustmentsPreviewUpdate = useCallback(
    (data: AdjustmentsData) => {
      setLiveAdjustments(data);
    },
    [],
  );

  // Estado do import compartilhado com os steps via context: o polling da
  // extração IA sobrevive à troca de passos (não bloqueia o avanço) e o
  // brief completo é conciliado quando chegar.
  const importDealState = useImportDealState();
  const { extractionStatus, fullBrief } = importDealState;

  // Conciliação pós-diagnóstico: quando o brief fica pronto DEPOIS que o
  // usuário já avançou, mescla só enriquecimento (importedData → payload da
  // proposta, insights, narrativa se "needs" vazio). Campos que afetam a
  // busca nunca são alterados aqui — ver reconcileDiagnosisWithBrief.
  // Enquanto o usuário está no passo 0, a hidratação local do DiagnosisStep
  // cuida (state.diagnosis ainda é null).
  const reconciledBriefRef = useRef<typeof fullBrief>(null);
  useEffect(() => {
    if (extractionStatus !== "ready" || !fullBrief) return;
    if (reconciledBriefRef.current === fullBrief) return;
    if (!state.diagnosis) return;
    reconciledBriefRef.current = fullBrief;
    updateDiagnosisPartial(
      reconcileDiagnosisWithBrief(state.diagnosis, fullBrief),
    );
    handlePreviewUpdate({ dealInsights: fullBrief.deal_insights ?? [] });
  }, [
    extractionStatus,
    fullBrief,
    state.diagnosis,
    updateDiagnosisPartial,
    handlePreviewUpdate,
  ]);

  const [isStepValid, setIsStepValid] = useState(false);
  const [previewSpaceId, setPreviewSpaceId] = useState<string | null>(null);
  const stepNextRef = useRef<(() => void) | null>(null);

  const registerNext = useCallback((fn: () => void) => {
    stepNextRef.current = fn;
  }, []);

  useEffect(() => {
    sendTrackDataServer({
      eventName: "VISION_AI_MODE_STARTED",
      productType: "PO",
      url: window.location.pathname,
      data: {
        // `|| null`: o stub da busca rápida tem dealId "" — não deve virar
        // um deal_id vazio no Segment.
        deal_id: state.diagnosis?.dealId || null,
        user_id: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
        has_previous_session: completedSteps.length > 0,
        resumed_at_step: currentStep,
      },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleQuickSearchStart() {
    sendTrackDataServer({
      eventName: "QUICK_SEARCH_USED",
      productType: "PO",
      url: window.location.pathname,
      data: {
        user_id: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
      },
    });
    enterQuickSearch();
  }

  function handleDiagnosisNext(data: DiagnosisData) {
    sendTrackDataServer({
      eventName: "VISION_AI_MODE_DIAGNOSIS_COMPLETED",
      productType: "PO",
      url: window.location.pathname,
      data: {
        deal_id: data.dealId || null,
        user_id: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
        company_name: data.companyName,
        contact_name: data.contactName,
        contact_email: data.contactEmail,
        segment: data.segment,
        target_city: data.targetCity,
        positions_min: data.positionsMin !== "" ? data.positionsMin : null,
        positions_max: data.positionsMax !== "" ? data.positionsMax : null,
        monthly_budget: data.monthlyBudget || null,
        has_deal_imported: !!data.importedData,
        has_needs: !!data.needs,
      },
    });
    updateDiagnosis(data);
    const diagnosisLocation =
      state.diagnosis?.selectedLocation ??
      data.selectedLocation ??
      (state.diagnosis?.targetCity
        ? { label: state.diagnosis.targetCity, lat: 0, lng: 0 }
        : null) ??
      (data.targetCity ? { label: data.targetCity, lat: 0, lng: 0 } : null);
    const searchParam = searchParams.get("search");
    const searchSourceFromUrl: SearchSource | null =
      searchParam === "filters"
        ? "filters"
        : searchParam === "search-and-rank"
          ? "search-and-rank"
          : searchParam === "search"
            ? "search"
            : null;
    const nextLocation: LocationData = {
      ...(state.location ?? {
        radiusKm: 5,
        selectedSpaceIds: [],
      }),
      location: diagnosisLocation,
      capacityMin: data.positionsMin !== "" ? Number(data.positionsMin) : null,
      capacityMax: data.positionsMax !== "" ? Number(data.positionsMax) : null,
      includeUnavailableWithinPeriod: data.includeUnavailableWithinPeriod,
      rankedSpaces: [],
      recommendationHash: undefined,
      searchSource:
        searchSourceFromUrl ??
        state.location?.searchSource ??
        getDefaultSearchSource(),
    };
    updateLocation(nextLocation);
    nextStep();
  }

  function handleLocationNext(data: LocationData) {
    sendTrackDataServer({
      eventName: "VISION_AI_MODE_LOCATION_COMPLETED",
      productType: "PO",
      url: window.location.pathname,
      data: {
        deal_id: state.diagnosis?.dealId ?? null,
        user_id: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
        company_name: state.diagnosis?.companyName ?? null,
        location_label: data.location?.label ?? null,
        radius_km: data.radiusKm,
        location_mode: data.locationMode ?? null,
        neighborhood_names: data.neighborhoodNames ?? null,
        selected_spaces_count: data.selectedSpaceIds.length,
        selected_space_ids: data.selectedSpaceIds,
        total_ranked_spaces: data.rankedSpaces?.length ?? 0,
        search_source: data.searchSource ?? undefined,
        tolerance_capacity: data.toleranceCapacity ?? null,
        tolerance_capacity_ignored: data.toleranceCapacityIgnored ?? false,
        tolerance_price: data.tolerancePrice ?? null,
        tolerance_price_ignored: data.tolerancePriceIgnored ?? false,
        tolerance_distance_km: data.toleranceDistanceKm ?? null,
        tolerance_distance_ignored: data.toleranceDistanceIgnored ?? false,
      },
    });
    updateLocation(data);
    nextStep();
  }

  function handleAdjustmentsNext(data: AdjustmentsData) {
    const adjs = data.spaceAdjustments;
    const adjCount = adjs.length;
    const avgMonthlyPrice =
      adjCount > 0
        ? Math.round(adjs.reduce((s, a) => s + a.totalPrice, 0) / adjCount)
        : 0;
    const avgSetupCost =
      adjCount > 0
        ? Math.round(
            adjs.reduce((s, a) => s + (a.setupCost ?? 0), 0) / adjCount,
          )
        : 0;
    const avgTotal =
      adjCount > 0
        ? Math.round(
            adjs.reduce((s, a) => s + a.totalPrice + (a.setupCost ?? 0), 0) /
              adjCount,
          )
        : 0;

    sendTrackDataServer({
      eventName: "VISION_AI_MODE_ADJUSTMENTS_COMPLETED",
      productType: "PO",
      url: window.location.pathname,
      data: {
        deal_id: state.diagnosis?.dealId ?? null,
        user_id: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
        company_name: state.diagnosis?.companyName ?? null,
        contract_months: data.contractMonths ?? null,
        credits_price: data.creditsPrice,
        selected_spaces_count: (data.selectedSpaces ?? []).length,
        recommended_spaces_count: (data.recommendedSpaceIds ?? []).length,
        avg_monthly_price: avgMonthlyPrice,
        avg_setup_cost: avgSetupCost,
        avg_total: avgTotal,
        proposal_validity_date: new Date(data.proposalValidity).toISOString(),
        has_seller_notes: !!data.sellerNotes,
      },
    });
    updateAdjustments(data);
    nextStep();
  }

  function handleImagesNext(data: AdjustmentsData) {
    const totalSelectedImages = (data.selectedSpaces ?? []).reduce(
      (sum, s) =>
        sum +
        (s.selectedImages?.length ?? s.images?.length ?? 0) +
        (s.selectedSpacePhotos?.length ?? s.spacePhotos?.length ?? 0),
      0,
    );
    sendTrackDataServer({
      eventName: "VISION_AI_MODE_IMAGES_COMPLETED",
      productType: "PO",
      url: window.location.pathname,
      data: {
        deal_id: state.diagnosis?.dealId ?? null,
        user_id: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
        company_name: state.diagnosis?.companyName ?? null,
        selected_spaces_count: (data.selectedSpaces ?? []).length,
        total_selected_images: totalSelectedImages,
      },
    });
    updateAdjustments(data);
    nextStep();
  }

  const displaySteps = useMemo(
    () =>
      editingProposal
        ? STEPS.map((s, i) =>
            i === STEPS.length - 1
              ? { ...s, label: "Resumo", icon: ClipboardCheck }
              : s,
          )
        : STEPS,
    [editingProposal],
  );

  const isLastStep = currentStep === STEPS.length - 1;
  const isImagesStep = currentStep === 3;
  const isLocationStep = currentStep === 1;

  // The rooms that end up in the proposal: individual picks ∪ every room of a
  // selected combo. Combo-only rooms live in `selectedComboIds`, not
  // `selectedSpaceIds`, so filtering by the latter alone drops them.
  const resolveProposalRoomIds = useCallback(
    (liveState: Partial<LocationData> | undefined): Set<string> => {
      const individualIds =
        liveState?.selectedSpaceIds ?? state.location?.selectedSpaceIds ?? [];
      const comboIds =
        liveState?.selectedComboIds ?? state.location?.selectedComboIds ?? [];
      const combinations =
        liveState?.roomCombinations ?? state.location?.roomCombinations ?? [];
      const ids = new Set(individualIds);
      for (const combo of combinations) {
        if (!comboIds.includes(combo.combinationId)) continue;
        for (const room of combo.rooms) ids.add(room.privateRoomId);
      }
      return ids;
    },
    [
      state.location?.selectedSpaceIds,
      state.location?.selectedComboIds,
      state.location?.roomCombinations,
    ],
  );

  const getTicketRooms = useCallback((): TicketRoom[] => {
    const liveState = getLocationFormStateRef.current?.();
    const proposalRoomIds = resolveProposalRoomIds(liveState);
    // Combo rooms can live in extraSpaces (the backend's extra_rooms), not just
    // rankedSpaces — include both so selected combo rooms aren't dropped.
    const spaces = [
      ...(liveState?.rankedSpaces ?? livePreview.rankedSpaces ?? []),
      ...(liveState?.extraSpaces ?? livePreview.extraSpaces ?? []),
    ];
    return spaces
      .filter((s) => proposalRoomIds.has(s.id))
      .map((s) => ({
        roomId: s.id,
        searchId: s.searchId ?? null,
        spaceId: s.partnerId ?? s.id,
        roomName: s.roomName,
        spaceName: s.spaceName,
        address: s.address,
        neighborhood: s.neighborhood,
        capacity: s.capacity,
        sizeM2: s.sizeM2,
      }));
  }, [
    resolveProposalRoomIds,
    livePreview.rankedSpaces,
    livePreview.extraSpaces,
  ]);

  const getTicketNeighborhoods = useCallback((): string[] => {
    const liveState = getLocationFormStateRef.current?.();
    const proposalRoomIds = resolveProposalRoomIds(liveState);
    const spaces = [
      ...(liveState?.rankedSpaces ?? livePreview.rankedSpaces ?? []),
      ...(liveState?.extraSpaces ?? livePreview.extraSpaces ?? []),
    ];
    const roomNeighborhoods = spaces
      .filter((s) => proposalRoomIds.has(s.id))
      .map((s) => s.neighborhood)
      .filter((n): n is string => Boolean(n));
    const searchNeighborhoods =
      liveState?.locationMode === "neighborhoods"
        ? (liveState.neighborhoodNames ??
          state.location?.neighborhoodNames ??
          [])
        : (state.location?.neighborhoodNames ?? []);
    return [...new Set([...roomNeighborhoods, ...searchNeighborhoods])];
  }, [
    resolveProposalRoomIds,
    livePreview.rankedSpaces,
    livePreview.extraSpaces,
    state.location?.neighborhoodNames,
  ]);

  function handleFooterNext() {
    stepNextRef.current?.();
  }

  function handleStepBack() {
    // Busca rápida é um estado à parte, não um passo do wizard: "Voltar"
    // encerra o modo e devolve o Diagnóstico limpo.
    if (isQuickMode) {
      exitQuickSearch();
      return;
    }
    sendTrackDataServer({
      eventName: "VISION_AI_MODE_STEP_BACK",
      productType: "PO",
      url: window.location.pathname,
      data: {
        deal_id: state.diagnosis?.dealId ?? null,
        user_id: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
        from_step: currentStep,
        from_step_name: STEPS[currentStep]?.label ?? "",
        to_step: currentStep - 1,
      },
    });
    if (currentStep === 1) {
      const partial = getLocationFormStateRef.current?.();
      if (partial) {
        updateLocationPartial(partial);
      }
    }
    if (currentStep === 0) {
      navigate(DEFAULT_ROUTES.VISION_360_BETA_SETUP);
    } else {
      prevStep();
    }
  }

  function performReset() {
    reset();
    setLivePreview(initialLivePreview);
    setLiveAdjustments(null);
    setShowNewSearchConfirmOpen(false);
    setResetKey((k) => k + 1);
  }

  function handleStartNewSearch(skipConfirmation?: boolean) {
    if (skipConfirmation) {
      performReset();
      return;
    }
    setShowNewSearchConfirmOpen(true);
  }

  if (isLoadingEdit) {
    return (
      <div
        className="fixed flex items-center justify-center"
        style={{
          top: 64,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10,
          background: "#FDFBF7",
          fontFamily: "'Space Grotesk', system-ui, sans-serif",
        }}
      >
        <div className="flex flex-col items-center gap-3">
          <div
            className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: "#FFD900", borderTopColor: "transparent" }}
          />
          <span className="text-sm" style={{ color: "#78736D" }}>
            Carregando proposta...
          </span>
        </div>
      </div>
    );
  }

  return (
    <ImportDealContext.Provider value={importDealState}>
      <div
        className="fixed flex flex-col"
        style={{
          top: 64,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10,
          fontFamily: "'Space Grotesk', system-ui, sans-serif",
        }}
      >
        {editingProposal && (
          <div
            className="flex-none w-full px-10 flex items-center justify-between gap-4"
            style={{
              background: "#FFD900",
              borderBottom: "1px solid #E8C800",
              minHeight: "40px",
            }}
          >
            <div className="flex-1">
              {currentStep === 0 && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      DEFAULT_ROUTES.VISION_PROPOSAL_DETAIL.replace(
                        ":uuid",
                        editingProposal.uuid,
                      ),
                    )
                  }
                  className="flex items-center gap-2 text-sm font-medium transition-colors"
                  style={{ color: "rgba(26,24,20,0.6)" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "#1A1814")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "rgba(26,24,20,0.6)")
                  }
                >
                  <ArrowLeft className="h-4 w-4" />
                  Voltar ao detalhe da proposta
                </button>
              )}
            </div>
            <div className="flex items-center gap-2.5 shrink-0">
              <div
                className="flex items-center gap-1.5 text-xs font-semibold"
                style={{ color: "#1A1814" }}
              >
                <PenLine className="w-3.5 h-3.5" />
                Modo de edição
              </div>
              {(editingProposal.state.diagnosis?.companyName ||
                editingProposal.versionNumber != null) && (
                <div
                  className="w-px self-stretch"
                  style={{ background: "rgba(26,24,20,0.2)" }}
                />
              )}
              {editingProposal.state.diagnosis?.companyName && (
                <span
                  className="text-xs font-medium max-w-[220px] truncate"
                  style={{ color: "#1A1814" }}
                >
                  {editingProposal.state.diagnosis.companyName}
                </span>
              )}
              {editingProposal.versionNumber != null && (
                <span
                  className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide"
                  style={{
                    background: "rgba(26,24,20,0.12)",
                    color: "#1A1814",
                  }}
                >
                  V{editingProposal.versionNumber}
                </span>
              )}
            </div>
          </div>
        )}
        <div
          className="flex-none w-full px-10 py-4 bg-white flex items-center justify-between gap-4"
          style={{ borderBottom: "1px solid #E8E6E3" }}
        >
          <div className="flex-1 min-w-0 flex justify-center">
            {isQuickMode ? (
              <div
                className="flex items-center gap-2 text-sm font-semibold"
                style={{ color: "#43403D" }}
              >
                <Zap className="w-4 h-4" />
                Busca rápida
              </div>
            ) : (
              <AIModeStepperBar
                currentStep={currentStep}
                completedSteps={completedSteps}
                steps={displaySteps}
              />
            )}
          </div>
          {!isLastStep && (
            <Button
              type="button"
              onClick={() => handleStartNewSearch(false)}
              className="flex-shrink-0 gap-1.5 px-4"
              style={{
                background: "#F0EEEB",
                color: "#1A1814",
                borderColor: "#E8E6E3",
              }}
            >
              Nova busca
            </Button>
          )}
        </div>

        <div className="flex flex-1 overflow-hidden flex-col lg:flex-row">
          <div
            className="relative overflow-hidden flex-none hidden lg:block"
            style={{ width: "60%", background: "#FDFBF7" }}
          >
            <div
              key={currentStep}
              className="w-full h-full"
              style={{
                animation:
                  "aiPreviewFade 0.3s cubic-bezier(0.16,1,0.3,1) forwards",
              }}
            >
              <PreviewPanel
                step={currentStep}
                preview={livePreview}
                state={state}
                liveAdjustments={liveAdjustments}
                onSpaceMarkerClick={(id) => onMarkerClickRef.current?.(id)}
                selectedSpaceId={previewSpaceId}
              />
            </div>
          </div>
          <div
            className="flex flex-col flex-1 lg:flex-none lg:w-[40%] bg-white overflow-hidden"
            style={{ borderLeft: "1px solid #E8E6E3" }}
          >
            <div
              className="lg:hidden w-full"
              style={{
                background: "#FDFBF7",
                borderBottom: "1px solid #E8E6E3",
                height: "200px",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              <PreviewPanel
                step={currentStep}
                preview={livePreview}
                state={state}
                liveAdjustments={liveAdjustments}
                onSpaceMarkerClick={(id) => onMarkerClickRef.current?.(id)}
                selectedSpaceId={previewSpaceId}
              />
            </div>
            <div
              key={`step-${currentStep}-${resetKey}`}
              className="flex-1 overflow-y-auto px-5 py-4 ai-wizard-scroll"
              style={{
                animation:
                  "aiStepSlide 0.35s cubic-bezier(0.16,1,0.3,1) forwards",
              }}
            >
              {currentStep === 0 && (
                <DiagnosisStep
                  data={state.diagnosis}
                  onNext={handleDiagnosisNext}
                  onPreviewUpdate={handlePreviewUpdate}
                  onValidityChange={setIsStepValid}
                  registerNext={registerNext}
                  isEditMode={!!editingProposal}
                  onQuickSearch={handleQuickSearchStart}
                />
              )}
              {currentStep === 1 && (
                <LocationStep
                  data={state.location}
                  diagnosis={state.diagnosis}
                  onNext={handleLocationNext}
                  onPreviewUpdate={handlePreviewUpdate}
                  onDiagnosisSync={handleDiagnosisSyncFromLocation}
                  onValidityChange={setIsStepValid}
                  registerNext={registerNext}
                  getFormStateRef={getLocationFormStateRef}
                  registerOnMarkerClick={(fn) => {
                    onMarkerClickRef.current = fn;
                  }}
                  originalSpaces={editOriginalSpaces}
                  isQuickMode={isQuickMode}
                />
              )}
              {currentStep === 2 && (
                <AdjustmentsStep
                  data={state.adjustments}
                  state={state}
                  onNext={handleAdjustmentsNext}
                  onChange={handleAdjustmentsPreviewUpdate}
                  onValidityChange={setIsStepValid}
                  registerNext={registerNext}
                  originalSpaces={editOriginalSpaces}
                  isEditMode={!!editingProposal}
                />
              )}
              {currentStep === 3 && (
                <ImagesStep
                  data={state.adjustments}
                  state={state}
                  onNext={handleImagesNext}
                  onChange={handleAdjustmentsPreviewUpdate}
                  onValidityChange={setIsStepValid}
                  registerNext={registerNext}
                  previewSpaceId={previewSpaceId}
                  onPreview={setPreviewSpaceId}
                />
              )}
              {currentStep === 4 &&
                (editingProposal ? (
                  <ReviewStep
                    state={state}
                    editingProposal={editingProposal}
                    onValidityChange={setIsStepValid}
                    registerNext={registerNext}
                  />
                ) : (
                  <ProposalStep
                    state={state}
                    onValidityChange={setIsStepValid}
                    onError={prevStep}
                    onStartNewSearch={() => handleStartNewSearch(true)}
                  />
                ))}
            </div>
          </div>
        </div>

        <div
          className="flex-none w-full flex items-center px-10 py-4 bg-white"
          style={{ borderTop: "1px solid #E8E6E3" }}
        >
          <div className="flex-1 flex items-center gap-3">
            {!(editingProposal && currentStep === 0) && (
              <Button
                type="button"
                variant="outline"
                onClick={handleStepBack}
                className="gap-1.5"
              >
                <ChevronLeft className="w-4 h-4" />
                Voltar
              </Button>
            )}
            {/* Status da extração IA visível ao longo do wizard (no passo 0 o
              DiagnosisStep tem os próprios banners, mais contextuais) */}
            {currentStep > 0 && extractionStatus === "pending" && (
              <span
                role="status"
                className="inline-flex items-center gap-2 text-xs rounded-full px-3 py-1.5"
                style={{
                  background: "#F5F3FF",
                  border: "1px solid rgba(167,139,250,0.25)",
                  color: "#6D28D9",
                }}
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                IA analisando o deal — a proposta usará a análise completa
              </span>
            )}
            {currentStep > 0 && extractionStatus === "failed" && (
              <span
                role="alert"
                className="inline-flex items-center gap-2 text-xs rounded-full px-3 py-1.5"
                style={{
                  background: "#FFFBEB",
                  border: "1px solid rgba(217,119,6,0.2)",
                  color: "#B45309",
                }}
              >
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                Análise IA não concluída — a proposta seguirá sem narrativa e
                insights (volte ao diagnóstico e reimporte para tentar de novo)
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {isLocationStep && !isQuickMode && (
              <Button
                type="button"
                variant="outline"
                className="gap-1.5 px-5"
                style={{
                  borderColor: "#E8E6E3",
                  color: "#43403D",
                  fontFamily: "'Space Grotesk', system-ui, sans-serif",
                }}
                onClick={() => setIsTicketSheetOpen(true)}
              >
                <ClipboardList className="w-4 h-4" />
                Abrir Ticket
              </Button>
            )}
            {/* Busca rápida é somente consulta: sem "Próximo", único caminho
                para os passos de proposta (PED-12650) */}
            {!isLastStep && !isQuickMode && (
              <Button
                type="button"
                onClick={handleFooterNext}
                // O brief completo só é consumido de fato no payload da
                // proposta (ProposalStep) — o gate fica aqui, no último clique
                // antes do persist, e não nos passos anteriores. "failed" não
                // bloqueia: a proposta sai degradada, com aviso no footer.
                disabled={
                  !isStepValid ||
                  (isImagesStep &&
                    !editingProposal &&
                    extractionStatus === "pending")
                }
                className="gap-1.5 px-8"
                style={
                  isStepValid
                    ? isImagesStep && !editingProposal
                      ? { background: "#1A1814", color: "white" }
                      : {
                          background: "#FFD900",
                          color: "#1A1814",
                          borderColor: "#FFD900",
                        }
                    : {}
                }
              >
                {isImagesStep && !editingProposal ? (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Gerar Proposta
                  </>
                ) : (
                  <>
                    Próximo
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        <TicketSheet
          open={isTicketSheetOpen}
          onClose={() => setIsTicketSheetOpen(false)}
          selectedRooms={isTicketSheetOpen ? getTicketRooms() : []}
          prefillCity={
            // targetCity is the raw places-search label ("Avenida Paulista -
            // Bela Vista, São Paulo - SP"), which the ticket's city format
            // rejects; blank beats a value that fails validation. PED-13058.
            formatCityUf(state.diagnosis?.targetCity) ??
            formatCityUf(state.location?.cityName) ??
            ""
          }
          prefillNeighborhoods={
            isTicketSheetOpen ? getTicketNeighborhoods() : []
          }
          prefillPositions={state.location?.capacityMin ?? null}
          dealId={state.diagnosis?.dealId}
          companyName={state.diagnosis?.companyName}
        />

        <Dialog
          open={showNewSearchConfirmOpen}
          onOpenChange={setShowNewSearchConfirmOpen}
        >
          <DialogContent
            showCloseButton={true}
            className="sm:max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <DialogHeader>
              <DialogTitle style={{ color: "#1A1814" }}>
                Iniciar nova busca?
              </DialogTitle>
              <DialogDescription style={{ color: "#78736D" }}>
                Os dados da busca atual serão perdidos e você voltará ao passo
                de diagnóstico. Deseja continuar?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewSearchConfirmOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={performReset}
                className="gap-1.5"
                style={{
                  background: "#FFD900",
                  color: "#1A1814",
                  borderColor: "#FFD900",
                }}
              >
                Sim, iniciar nova busca
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ImportDealContext.Provider>
  );
}
